import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import industryPages from '../data/industryPages.json';
import { ArrowRight, AlertCircle, Phone } from 'lucide-react';

export default function IndustryPage() {
  const { industry } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const data = industryPages.find((i) => i.slug === industry);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, [industry]);

  if (!data) return <Navigate to="/" replace />;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://harpalyk.com/solutions/${data.slug}`} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid={`industry-page-${data.slug}`}>
        <Breadcrumbs items={[{ label: 'Solutions' }, { label: data.name }]} />

        <section className="py-16 lg:py-24 px-6 lg:px-12 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-600 uppercase mb-4">Industry Solutions</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-8">{data.h1}</h1>
            {data.intro.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-gray-600 leading-relaxed mb-5">{p}</p>
            ))}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="/#contact" data-testid="industry-demo-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 text-white px-8 py-4 text-base font-medium hover:bg-gray-700 transition-colors duration-300">
                Request a Consultation <ArrowRight size={17} />
              </a>
              <a href="tel:+917011370322" className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 text-gray-900 px-8 py-4 text-base font-medium hover:border-gray-900 transition-colors duration-300">
                <Phone size={16} /> Talk to an Expert
              </a>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Challenges We Solve</h2>
              <ul className="space-y-3">
                {data.challenges.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Recommended Robots</h2>
              <div className="space-y-4" data-testid="industry-products">
                {data.products.map((p) => (
                  <a key={p.name} href={p.href} className="group block rounded-2xl border border-black/5 bg-[#F8F9FA] p-5 hover:bg-white hover:shadow-lg transition-[box-shadow,background-color] duration-300">
                    <h3 className="font-heading text-base font-semibold text-gray-900 mb-1 inline-flex items-center gap-2">
                      {p.name} <ArrowRight size={14} className="text-emerald-600 group-hover:translate-x-1 transition-transform duration-300" />
                    </h3>
                    <p className="text-sm text-gray-600">{p.why}</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-20 px-6 lg:px-12 bg-[#F8F9FA]">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {data.faq.map((f, i) => (
                <div key={i} className="rounded-2xl border border-black/5 bg-white p-6">
                  <h3 className="font-heading text-base font-semibold text-gray-900 mb-2">{f.q}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-sm text-gray-500">
              Other industries:{' '}
              {industryPages.filter((i) => i.slug !== data.slug).slice(0, 6).map((i2, idx, arr) => (
                <span key={i2.slug}>
                  <Link to={`/solutions/${i2.slug}`} className="text-emerald-700 hover:underline">{i2.name}</Link>
                  {idx < arr.length - 1 ? ' · ' : ''}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
